import React, { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import { Dialog as DialogType, DialogSection, DialogVariable } from '@/lib/stores/dialogStore';
import { Node, Edge, Panel } from '@xyflow/react';
import { FlowCanvas } from '@/components/flow/FlowCanvas';
import { AutoLayoutControls } from '@/components/flow/nodes/node-controls/AutoLayoutControls';
import { BulkEdgeStyleControls } from '@/components/flow/nodes/node-controls/BulkEdgeStyleControls';
import { HelperLinesControls } from '@/components/flow/nodes/node-controls/HelperLinesControls';
import { dialogFlowConfig } from '@/components/flow/configs/dialogFlowConfig';
import { migrateDialogSectionsToNodes } from '../utils/dialogUtils';
import { ConnectionContextMenu } from '@/components/flow/ConnectionContextMenu';
import { useZoom } from '@/providers/ZoomProvider';
import { ContextMenuState, SpeakerMode, ViewMode } from '../types';
import { DialogViewModeSelector } from './DialogViewModeSelector';

export interface DialogFlowViewProps {
  // Layout
  inline: boolean;
  
  // Data
  sections: DialogSection[];
  localFlowSections: DialogSection[];
  flowId: string | null;
  flowNodes: Node[];
  flowEdges: Edge[];
  flowViewport: { x: number; y: number; zoom: number } | null;
  effectiveGameId: string;
  
  // Dialog metadata for flow generation
  initialData?: {
    id?: string;
    tags?: string[];
    folder?: string | null;
    status?: string;
    created_at?: string;
    updated_at?: string;
  } | null;
  dialogName: string;
  dialogKey: string;
  description: string;
  translatable: boolean;
  speakerMode: SpeakerMode;
  speakerTypeId: string | null;
  speakerTemplateIds: string[];
  dialogVariables: DialogVariable[];
  
  // Context menu state
  contextMenu: ContextMenuState;
  
  // View mode
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  
  // Flow handlers
  onConnect: (connection: any) => void;
  onConnectEnd: (event: any, connectionState: any, flowPosition?: { x: number; y: number }) => void;
  onPaneContextMenu: (event: React.MouseEvent, flowPosition: { x: number; y: number }) => void;
  onNodesChange: (nodes: Node[]) => void;
  onEdgesChange: (edges: Edge[]) => void;
  onViewportChange: (viewport: { x: number; y: number; zoom: number }) => void;
  onDrop: (event: React.DragEvent, nodes: Node[]) => Node | null;
  onDragOver: (event: React.DragEvent) => void;
  onNodeClick?: (node: Node) => void;
  
  // Context menu handlers
  onCreateFieldFromConnection: (fieldType: string) => void;
  onContextMenuClose: () => void;
  
  // Node callback injection
  addCallbackToNodes: (nodes: Node[]) => Node[];
}

/**
 * Flow view component for DialogWizard
 * Encapsulates the FlowCanvas and related modals
 */
export function DialogFlowView({
  inline,
  sections,
  localFlowSections,
  flowId,
  flowNodes,
  flowEdges,
  flowViewport,
  effectiveGameId,
  initialData,
  dialogName,
  dialogKey,
  description,
  translatable,
  speakerMode,
  speakerTypeId,
  speakerTemplateIds,
  dialogVariables,
  contextMenu,
  viewMode,
  onViewModeChange,
  onConnect,
  onConnectEnd,
  onPaneContextMenu,
  onNodesChange,
  onEdgesChange,
  onViewportChange,
  onDrop,
  onDragOver,
  onNodeClick,
  onCreateFieldFromConnection,
  onContextMenuClose,
  addCallbackToNodes,
}: DialogFlowViewProps) {
  // Get zoom level to counteract platform scaling
  const { zoom } = useZoom();
  const inverseZoom = 100 / zoom;
  
  // Helper lines state
  const [helperLinesEnabled, setHelperLinesEnabled] = useState(false);
  
  // Track when the context menu was opened to prevent immediate closure
  const contextMenuOpenedAtRef = useRef<number>(0);
  
  // Update the timestamp when context menu is opened or position changes
  // This ensures that dragging a new connection while menu is open resets the grace period
  useEffect(() => {
    if (contextMenu.show) {
      contextMenuOpenedAtRef.current = Date.now();
    }
  }, [contextMenu.show, contextMenu.position.x, contextMenu.position.y]);
  
  // Track FlowCanvas key for re-rendering - use stable key to prevent unnecessary remounting
  // Only change key when actually loading a different dialog (flowId changes) or initialData.id changes
  const flowCanvasKey = flowId || initialData?.id || 'new-dialog';
  
  // Compute initial nodes for FlowCanvas
  const flowCanvasInitialNodes = useMemo(() => {
    const nodes = flowNodes.length > 0 ? flowNodes : addCallbackToNodes(dialogFlowConfig.dataToNodes({
      id: initialData?.id || '',
      game_id: effectiveGameId,
      name: dialogName,
      dialog_key: dialogKey,
      description,
      tags: initialData?.tags || [],
      folder: initialData?.folder,
      status: (initialData?.status as 'draft' | 'published') || 'draft',
      data: { 
        sections: localFlowSections.length > 0 ? localFlowSections : sections,
        translatable, 
        speakerMode, 
        speakerTypeId, 
        speakerTemplateIds,
        variables: dialogVariables,
      },
      created_at: initialData?.created_at || '',
      updated_at: initialData?.updated_at || '',
    } as DialogType));
    return nodes;
  }, [flowNodes, localFlowSections, sections, initialData, effectiveGameId, dialogName, dialogKey, description, translatable, speakerMode, speakerTypeId, speakerTemplateIds, dialogVariables, addCallbackToNodes]);
  
  // Compute initial edges for FlowCanvas
  const flowCanvasInitialEdges = useMemo(() => {
    const edges = flowEdges.length > 0 ? flowEdges : dialogFlowConfig.dataToEdges({
      id: initialData?.id || '',
      game_id: effectiveGameId,
      name: dialogName,
      dialog_key: dialogKey,
      description,
      tags: initialData?.tags || [],
      folder: initialData?.folder,
      status: (initialData?.status as 'draft' | 'published') || 'draft',
      data: migrateDialogSectionsToNodes({ 
        sections: localFlowSections.length > 0 ? localFlowSections : sections,
        translatable, 
        speakerMode, 
        speakerTypeId, 
        speakerTemplateIds,
        variables: dialogVariables,
      }),
      created_at: initialData?.created_at || '',
      updated_at: initialData?.updated_at || '',
    } as DialogType);
    return edges;
  }, [flowEdges, localFlowSections, sections, initialData, effectiveGameId, dialogName, dialogKey, description, translatable, speakerMode, speakerTypeId, speakerTemplateIds, dialogVariables]);

  // Handle pane click - close context menu when clicking on canvas
  // BUT respect the grace period to allow menu to appear from connection drags
  const handlePaneClick = useCallback(() => {
    if (contextMenu.show) {
      const timeSinceOpened = Date.now() - contextMenuOpenedAtRef.current;
      // Only close if menu has been open for more than 150ms (grace period)
      if (timeSinceOpened > 150) {
        onContextMenuClose();
      }
    }
  }, [contextMenu.show, onContextMenuClose]);

  // Handle node click - close context menu and call original handler
  // BUT respect the grace period to allow menu to appear from connection drags
  const handleNodeClick = useCallback((node: Node) => {
    if (contextMenu.show) {
      const timeSinceOpened = Date.now() - contextMenuOpenedAtRef.current;
      // Only close if menu has been open for more than 150ms (grace period)
      if (timeSinceOpened > 150) {
        onContextMenuClose();
      }
    }
    onNodeClick?.(node);
  }, [contextMenu.show, onContextMenuClose, onNodeClick]);

  return (
    <div className={inline ? "flex-1 w-full relative" : "flex-1 relative"}>
      {/* View Mode Selector - positioned absolutely on top of the flow canvas */}
      <div className={inline ? "absolute top-0 left-0 right-0 z-10 max-w-6xl mx-auto px-6 pt-4 pb-2 w-full pointer-events-none" : "absolute top-0 left-0 right-0 z-10 px-6 pt-4 pb-2 pointer-events-none"}>
        <div className="pointer-events-auto w-fit">
          <DialogViewModeSelector
            viewMode={viewMode}
            onViewModeChange={onViewModeChange}
            inline={false}
            absolute={true}
          />
        </div>
      </div>

      <div 
        className="w-full h-full" 
        style={{ 
          minHeight: '600px', 
          height: inline ? '100%' : 'calc(100vh - 250px)',
          zoom: `${inverseZoom}%`
        }}
      >
        {sections.length > 0 ? (
          <FlowCanvas
            key={flowCanvasKey}
            initialNodes={flowCanvasInitialNodes}
            initialEdges={flowCanvasInitialEdges}
            nodeTypes={dialogFlowConfig.nodeTypes}
            edgeTypes={dialogFlowConfig.edgeTypes}
            showControls={true}
            showMiniMap={true}
            showBackground={true}
            showDetailsPanel={false}
            fitViewOnLoad={!flowViewport}
            defaultViewport={flowViewport || undefined}
            onViewportChange={onViewportChange}
            validateConnection={dialogFlowConfig.validateConnection}
            onConnect={onConnect}
            onConnectEnd={onConnectEnd}
            onPaneContextMenu={onPaneContextMenu}
            onPaneClick={handlePaneClick}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={handleNodeClick}
            allowDragAndDrop={true}
            onDrop={onDrop}
            onDragOver={onDragOver}
            helperLinesEnabled={helperLinesEnabled}
            onHelperLinesToggle={setHelperLinesEnabled}
            enableUndoRedo={true}
          >
            <Panel position="top-right" className="m-2">
              <div className="flex gap-1">
                <HelperLinesControls 
                  withPanel={false} 
                  enabled={helperLinesEnabled}
                  onToggle={setHelperLinesEnabled}
                />
                <BulkEdgeStyleControls withPanel={false} />
                <AutoLayoutControls withPanel={false} />
              </div>
            </Panel>
          </FlowCanvas>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-lg text-muted-foreground mb-2">No Sections</p>
              <p className="text-sm text-muted-foreground">
                Add sections in Edit Mode to start building your dialog flow
              </p>
            </div>
          </div>
        )}
      </div>
      
      {/* Connection Context Menu - Rendered outside zoomed container */}
      {contextMenu.show && (
        <ConnectionContextMenu
          position={contextMenu.position}
          onSelect={onCreateFieldFromConnection}
          onClose={onContextMenuClose}
        />
      )}
    </div>
  );
}

